import React, { useState, useEffect, useRef } from "react";
import BreadcrumbDetailed from "components/Common/BreadcrumbDetailed";
import { userRegister, clearRegisterMessage } from "redux/actions";
import { userLogin } from "redux/actions/user";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import "./signup.scss";
import { getLocalStorageValue } from "config/helper";
import { Clients } from 'config/constant';

const SignupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentStoreID } = useSelector(({ store }) => store);
  const storeId = currentStoreID || getLocalStorageValue("selected-store-id");
  const clientId = getLocalStorageValue("RSAclient-id");
  const client_name = getLocalStorageValue("client-name");
  const storeList = useSelector(({ store }) => store.storeListData);

  const { registerStatus, registerMessage, registerLoading, loginLoading } = useSelector(({ user }) => user);

  const thirdPartyLogin = useSelector(
    ({ common }) => common.commonDetailsData.third_party_login_section
  );

  const headerSection = useSelector(
    ({ common }) => common.commonDetailsData.header_section
  );

  const icontactOptions = useSelector(
    ({ common }) => common.commonDetailsData?.icontact_stay_connected_option
  );

  const [selectedStore, setSelectedStore] = useState(storeId);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleSelectStore = (event) => {
    setSelectedStore(event.target.value);
  };

  const isRedners = clientId === Clients.Redners;

  const {
    allow_link_card_registration,
    member_card_max_length,
    member_card_min_length,
    loyalty_card_message,
    numeric_password_length,
    complex_password_max_length,
    complex_password_min_length,
  } = headerSection;

  let is_numeric_password = headerSection?.is_numeric_password
  let minLength = is_numeric_password === "1" ? +numeric_password_length : +complex_password_min_length
  let maxLength = is_numeric_password === "1" ? +numeric_password_length : +complex_password_max_length
  if (isRedners) {
    minLength = +complex_password_min_length
    maxLength = +complex_password_max_length
    is_numeric_password = "1"
  }  

  const {
    allow_apple_login,
    allow_facebook_login,
    allow_google_login,
    allow_linkcard_login
  } = thirdPartyLogin;

  const isShowThirdPartyLoginSection =
    Object.values(thirdPartyLogin).includes("1");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    zipCode: "",
    password: "",
    confirmPassword: "",
    mobile: "",
    loyaltyNumber: "",
    confirmLoyaltyNumber: "",
    termsAccepted: false,
    icontactSelected: []
  });
  
  const [formErrors, setFormErrors] = useState({});
  const registrationCredentialsRef = useRef(null);
  const hasAutoLoginAttemptedRef = useRef(false);
 
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const errors = validateForm();
    if (errors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: errors[name]
      }));
    } else {
      setFormErrors((prev) => {
        const { [name]: removedError, ...rest } = prev;
        return rest;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.firstName) errors.firstName = "Please enter your First Name";
    if (!formData.lastName) errors.lastName = "Please enter your Last Name";
    if (!/^[0-9]{5}$/.test(formData.zipCode))
      errors.zipCode = "Please enter a valid Zip Code";
    if (!/^[0-9]{10}$/.test(formData.mobile))
      errors.mobile = "Please enter a valid 10-digit Phone Number";

    if (is_numeric_password === "1") {
      if (!/^[0-9]+$/.test(formData.password))
        errors.password = "PIN must contain only numbers";
      if (formData.password.length !== +minLength)
        errors.password = `PIN must be ${minLength} digits long`;
    } else {
      if (
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/.test(
          formData.password
        )
      )
        errors.password =
          "Password must include 1 uppercase, 1 lowercase, 1 number, and 1 special character";
      if (
        formData.password.length < +complex_password_min_length ||
        formData.password.length > +complex_password_max_length
      )
        errors.password = `Password must be between ${complex_password_min_length} and ${complex_password_max_length} characters`;
    }

    if (formData.password !== formData.confirmPassword)
      errors.confirmPassword = "Passwords do not match";
    if (
      formData.loyaltyNumber &&
      formData.loyaltyNumber !== formData.confirmLoyaltyNumber
    )
      errors.confirmLoyaltyNumber = "Loyalty Numbers do not match";
    if (!formData.termsAccepted)
      errors.termsAccepted = "Please accept Terms and Conditions";

    setIsFormValid(Object.keys(errors).length === 0);
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      const body = {
        RSAClientId: clientId,
        ClientStoreId: selectedStore || 1,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email_id: `${formData.mobile}@example.com`,
        password: formData.password,
        zipcode: formData.zipCode,
        mobile: formData.mobile,
        member_number: formData.loyaltyNumber || "",
        icontact_selected_value: formData.icontactSelected,
        device_type: "pwa-web",
        Version: "1"
      };
      // Store credentials for auto-login after successful registration
      registrationCredentialsRef.current = {
        mobile: formData.mobile,
        password: formData.password
      };
      hasAutoLoginAttemptedRef.current = false;
      dispatch(userRegister(body));
    } else {
      setFormErrors(errors);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearRegisterMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    const errors = validateForm();
    const isFormValidNow = Object.keys(errors).length === 0;
    setIsFormValid(isFormValidNow);
  }, [formData, validateForm]);

  const RSA_API_Version = useSelector(({ common }) => common.commonDetailsData?.header_section?.RSA_API_Version ?? "V2");
  useEffect(() => {
    if (registerStatus === "success" && registrationCredentialsRef.current && !hasAutoLoginAttemptedRef.current) {
      // Prevent multiple login attempts
      hasAutoLoginAttemptedRef.current = true;
      
      // Auto-login the user after successful registration
    const { mobile, password } = registrationCredentialsRef.current;
    const is_mobile_number = mobile?.includes('@') ? "0" : "1";
    const loginBody = {
        RSAClientId: clientId,
        email_id: `${mobile}`,
        password: password,
        is_mobile_number
      };
      
      const handleLoginSuccess = () => {
        // Clear form data after successful login
        setFormData({
          firstName: "",
          lastName: "",
          zipCode: "",
          email: "",
          password: "",
          confirmPassword: "",
          mobile: "",
          loyaltyNumber: "",
          confirmLoyaltyNumber: "",
          termsAccepted: false,
          icontactSelected: []
        });
        // Clear the refs
        registrationCredentialsRef.current = null;
        hasAutoLoginAttemptedRef.current = false;
        // Navigate to home page without refresh
        navigate("/");
      };
      
      // Call userLogin action
      dispatch(userLogin(loginBody, handleLoginSuccess, RSA_API_Version));
    }
  }, [registerStatus, dispatch, navigate, clientId]);

  const breadcrumbList = [{ lable: "Sign Up", link: "" }];

  return (
    <>
      <BreadcrumbDetailed breadcrumbList={breadcrumbList} title={`Sign Up`} />
      <div className="container">
        <div className="signup-page-wrapper">
          <div className="auth-grid">
            <div className="auth-grid-items">
              <h1>Create an account</h1>
              <p>
                Already have an account?{" "}
                <span>
                  <Link to="/login">Sign In</Link>
                </span>
              </p>
              {(registerLoading || loginLoading) && (
                <div className="loading-spinner">
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  <span>
                    {registerLoading 
                      ? "Creating your account.. Please wait."
                      : "Logging you in.. Please wait."
                    }
                  </span>
                </div>
              )}
              {registerStatus && !loginLoading ? (
                <span
                  className={
                    registerStatus === "success"
                      ? "register-success"
                      : "register-error"
                  }>
                  {registerStatus === "success" ? (
                    <span>
                      You have been registered successfully. Logging you in...
                    </span>
                  ) : (
                    `Failed to register - ${registerMessage}`
                  )}
                </span>
              ) : (
                ""
              )}
              <div className="signup-form">
                <form onSubmit={handleSubmit}>
                  <div className="name-wrapper">
                    <div className="form-control">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {formErrors.firstName && <span>{formErrors.firstName}</span>}
                    </div>
                    <div className="form-control">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {formErrors.lastName && <span>{formErrors.lastName}</span>}
                    </div>
                  </div>

                  <div className="zip-code-wrapper">
                    <div className="form-control">
                      <label>Zip Code (5 digits) *</label>
                      <input
                        name="zipCode"
                        type="text"
                        maxLength={5}
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {formErrors.zipCode && <span>{formErrors.zipCode}</span>}
                    </div>
                    <div className="form-control store-drop-down">
                      <label>Preferred Store *</label>
                      <select
                        value={selectedStore}
                        name="selectStore"
                        onChange={handleSelectStore}
                        className="form-control show-items dropdown-list-align select-dropdown">
                        {storeList.length > 0 &&
                          storeList.map((store) => {
                            return (
                              <option value={store.ClientStoreId}>
                                {store.ClientStoreName}
                              </option>
                            );
                          })}
                      </select>
                    </div>
                  </div>
                  <br />
                  <br />
                  <div className="form-control">
                    <label>Phone Number (10 digits) *</label>
                    <input
                      name="mobile"
                      type="text"
                      maxLength={10}
                      value={formData.mobile}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                    {formErrors.mobile && <span>{formErrors.mobile}</span>}
                  </div>
                  <div className="form-control">
                    {is_numeric_password === "1" ? (
                      <div className="form-control">
                        <label>Pin *{isRedners ? "" : ` (${minLength} Digits)`}</label>
                        <input
                          name="password"
                          type="password"
                          maxLength={maxLength}
                          minLength={minLength}
                          value={formData.password}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                        />
                        {formErrors.password && <span>{formErrors.password}</span>}
                      </div>
                    ) : (
                      <div className="form-control">
                        <label>
                          Password *
                          <span style={{ color: "grey" }}>
                            {"(" +
                              minLength +
                              -+maxLength}{" "}
                            characters long. 1 Capital letter, 1 small letter, 1
                            Special character, 1 numeric required.)
                          </span>
                        </label>
                        <input
                          name="password"
                          type="password"
                          maxLength={maxLength}
                          minLength={minLength}
                          value={formData.password}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                        />
                        {formErrors.password && <span>{formErrors.password}</span>}
                      </div>
                    )}

                    <div className="form-control">
                      {is_numeric_password === "1" ? (
                        <label>Re-enter PIN *</label>
                      ) : (
                        <label>Re-enter Password *</label>
                      )}

                      <input
                        name="confirmPassword"
                        type="password"
                        maxLength={maxLength}
                        minLength={minLength}
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {formErrors.confirmPassword && (
                        <span>{formErrors.confirmPassword}</span>
                      )}
                    </div>
                  </div>
                  {allow_link_card_registration === "1" && (
                    <div className="loyalty-card">
                      <hr />
                      <label className="loyalty-card-label">
                        {loyalty_card_message}
                      </label>
                      <div className="loyalty-card-wrapper">
                        <div className="form-control">
                          <label>Enter Loyalty Card#</label>
                          <input
                            type="text"
                            name="loyaltyNumber"
                            minLength={member_card_min_length}
                            maxLength={member_card_max_length}
                            value={formData.loyaltyNumber}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          />
                        </div>
                        <div className="form-control">
                          <label>Re-enter Loyalty Card# *</label>
                          <input
                            type="text"
                            name="confirmLoyaltyNumber"
                            minLength={member_card_min_length}
                            maxLength={member_card_max_length}
                            value={formData.confirmLoyaltyNumber}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          />
                          {formErrors.confirmLoyaltyNumber && (
                            <span>{formErrors.confirmLoyaltyNumber}</span>
                          )}
                        </div>
                      </div>
                      <hr />
                    </div>
                  )}
                  <div className="loyalty-card">
                    {headerSection &&
                      headerSection?.Allow_IContact_Integration === "1" || headerSection?.allow_constant_contact_form === "1" && (
                        <div className="form-control">
                          <h4 style={{ marginBottom: "8px" }}>
                            Stay Connected{" "}
                          </h4>
                          <p className="connected-message" style={{ marginBottom: "10px" }}>
                            Verify the type of email promotions you would like
                            to receive from {headerSection?.client_name} by
                            checking the boxes below
                          </p>
                          {icontactOptions &&
                            icontactOptions?.map((each) => (
                              <div className="check-wrapper">
                                <div key={each?.slug}>
                                  <input
                                    type="checkbox"
                                    name="icontactSelected"
                                    id={each?.slug}
                                    value={each?.slug}
                                    checked={formData.icontactSelected.includes(each?.slug)}
                                    onChange={(e) => {
                                      const { checked, value } = e.target;
                                      setFormData((prev) => ({
                                        ...prev,
                                        icontactSelected: checked
                                          ? [...prev.icontactSelected, value]
                                          : prev.icontactSelected.filter((item) => item !== value)
                                      }));
                                    }}
                                  />
                                  <label htmlFor={each?.slug} className="signup-checkbox-wrap">
                                    {each?.title}
                                  </label>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}

                    <hr />
                  </div>
                  <div className="terms-conditions-wrapper">
                    <div className="terms-and-conditions-wrapper">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        id="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="termsAccepted">
                        <span> &nbsp; &nbsp;I agree to the {client_name}</span>
                        <a
                          href="/custom-pages/terms-conditions"
                          rel="noreferrer"
                          target="_blank">
                          &nbsp;Terms and Conditions
                        </a>
                        <span>&nbsp;and</span>
                        <a
                          href="/custom-pages/privacy-policy"
                          rel="noreferrer"
                          target="_blank">
                          &nbsp;Privacy policy.
                        </a>
                      </label>
                    </div>
                    {formErrors.termsAccepted && (
                      <span style={{ fontSize: "11px", color: "#DA2327" }}>
                        {formErrors.termsAccepted}
                      </span>
                    )}
                  </div>
                  <div className="sign-up-button">
                    <button
                      type="submit"
                      disabled={!isFormValid}
                      className={!isFormValid ? "disabled" : ""}
                    >
                      Create Account
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="auth-grid-items">
              {isShowThirdPartyLoginSection && (
                <div className="signup-option">
                  {allow_facebook_login === "1" && (
                    <div className="facebook-button">
                      <button>
                        <i className="fa-brands fa-facebook"></i>
                        <span>Continue with Facebook</span>
                      </button>
                    </div>
                  )}
                  {allow_google_login === "1" && (
                    <div className="google-button">
                      <button>
                        <i className="fa-brands fa-google"></i>
                        <span>Continue with google</span>
                      </button>
                    </div>
                  )}
                  {allow_apple_login === "1" && (
                    <div className="apple-login">
                      <button>
                        <i className="fa-brands fa-apple"></i>
                        <span>Continue with Apple</span>
                      </button>
                    </div>
                  )}
                  {allow_linkcard_login === "1" && (
                    <div className="card-registration">
                      <button>Link Card Registration</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupPage;
